import { clientBuilderProductDisplayNames } from "api-types/builders/buildersResponses";
import type { BuilderProduct } from "api-types/builders/builderTypes";
import { builderProductValues } from "api-types/builders/builderTypes";
import type { ClientBuilderUserPermission } from "api-types/builderUsers/builderUsersBaseTypes";
import type { ClientFeatureFlagPublicData } from "api-types/featureFlags/featureFlagsResponses";
import { FALLBACK_VALUE } from "common/constants";
import type { FeatureName, FeatureSet } from "common/constants/features";
import { featureNames, features } from "common/constants/features";
import { getClientEnvironment } from "common/utils/env";
import { filterFeatureSet } from "common/utils/features";
import objectFilter from "common/utils/objectFilter";
import { camelCaseToTitleCase } from "common/utils/strings";
import {
  featureFlagSettingOptions,
  selectionToSetting,
  settingToSelection,
} from "lib/components/admin/features/helpers";
import { useTheme } from "lib/components/studio/useTheme";
import { getFeatureFlagPublicDataURL } from "lib/endpointHelpers";
import { useFetchNoAuth } from "lib/fetcher";
import { formatPermissionName, getVisiblePermissionGroups } from "lib/security/permissionUtils";
import type { FeatureEnabledReason } from "lib/security/shared";
import { featureEnabled } from "lib/security/shared";
import { useUserContext } from "lib/state/UserContextProvider";
import { putUser } from "lib/storage/userStorage";
import { copyTextWithNotification } from "lib/utils/clipboard";
import { useCallback, useEffect, useMemo, useState } from "react";
import Button from "ui/buttons/Button";
import CodeBlock from "ui/components/CodeBlock";
import Modal from "ui/components/Modal";
import cx from "ui/cx";
import type { OptionProps } from "ui/forms/Select";
import Select from "ui/forms/Select";
import Switch from "ui/forms/Switch";
import useAppContext from "ui/hooks/useAppContext";
import IconCheck from "ui/icons/IconCheck";
import IconClose from "ui/icons/IconClose";
import IconCopy from "ui/icons/IconCopy";
import { Popover } from "ui/popovers/Popover";
import { ConfigTable } from "ui/tables/DataTable";
import type { Column } from "ui/tables/DataTable/types";
import { Message } from "ui/text/Message";
import styles from "./DebugInfoModal.module.scss";

interface DebugInfoModalProps {
  open: boolean;
  onClose: () => void;
}

type FeatureRow = {
  name: FeatureName;
  enabled: boolean;
  reason: FeatureEnabledReason;
};

type ProductRow = {
  name: string;
  product: BuilderProduct;
  enabled: boolean;
};

type PermissionRow = {
  group: string;
  permission: ClientBuilderUserPermission;
  enabled: boolean;
};

type DebugInfoView = "overview" | "featureFlags" | "products" | "access";

function getEnvironmentDetail(value: string | number | boolean | undefined | null): string {
  if (typeof value === "boolean") {
    return value ? "true" : "false";
  }

  if (typeof value === "number") {
    return String(value);
  }

  if (!value) {
    return FALLBACK_VALUE;
  }

  return value;
}

export default function DebugInfoModal({ open, onClose }: DebugInfoModalProps) {
  const { user, builder, builderUser, builderPoliciesAndRoles, hasPermission, enabledProducts, enabledFeatures } =
    useUserContext();
  const { setUserNotification } = useAppContext();
  // Falls back to light when rendered outside Studio's ThemeProvider (builder portal top nav).
  const { isDarkMode } = useTheme();
  const environment = getClientEnvironment();
  const [hideDisabled, setHideDisabled] = useState(false);
  const [hideDisabledProducts, setHideDisabledProducts] = useState(false);
  const [hideDeniedPermissions, setHideDeniedPermissions] = useState(false);
  const [view, setView] = useState<DebugInfoView>("overview");
  const [userFeatureFlagOverrides, setUserFeatureFlagOverrides] = useState<FeatureSet>({});
  const [savingFeatureName, setSavingFeatureName] = useState<FeatureName | null>(null);
  const { data: featureFlagPublicData } = useFetchNoAuth<ClientFeatureFlagPublicData>(
    getFeatureFlagPublicDataURL({ builderId: builder?.id }),
  );

  useEffect(() => {
    // Drop retired/unknown flag keys from the stored JSON so saving doesn't
    // send keys the API schema rejects (matches the admin lists' behavior)
    setUserFeatureFlagOverrides(filterFeatureSet(user?.featureFlags ?? {}));
  }, [user?.featureFlags]);

  const userWithFeatureFlagOverrides = useMemo(
    () => (user ? { ...user, featureFlags: userFeatureFlagOverrides } : user),
    [user, userFeatureFlagOverrides],
  );

  const updateUserFeatureFlag = useCallback(
    async (featureName: FeatureName, newSetting: boolean | undefined): Promise<void> => {
      if (!user?.id) {
        return;
      }

      const previousSetting = userFeatureFlagOverrides[featureName];
      const nextFeatureFlags: FeatureSet = { ...userFeatureFlagOverrides };
      if (typeof newSetting === "boolean") {
        nextFeatureFlags[featureName] = newSetting;
      } else {
        delete nextFeatureFlags[featureName];
      }

      setUserFeatureFlagOverrides(nextFeatureFlags);
      setSavingFeatureName(featureName);
      const [response, message] = await putUser(user.id, { featureFlags: nextFeatureFlags });
      setSavingFeatureName(null);

      if (message || !response) {
        setUserFeatureFlagOverrides((currentFeatureFlags) => {
          const revertedFeatureFlags = { ...currentFeatureFlags };
          if (typeof previousSetting === "boolean") {
            revertedFeatureFlags[featureName] = previousSetting;
          } else {
            delete revertedFeatureFlags[featureName];
          }
          return revertedFeatureFlags;
        });
        setUserNotification(
          message ?? {
            messageType: "error",
            message: `Failed to update ${camelCaseToTitleCase(featureName)} setting. Please try again.`,
          },
        );
        return;
      }

      setUserNotification({
        messageType: "success",
        message: `Updated ${camelCaseToTitleCase(
          featureName,
        )} setting for this user. Refresh to apply it across the app.`,
      });
    },
    [setUserNotification, user?.id, userFeatureFlagOverrides],
  );

  const featureColumns: Column<FeatureRow>[] = useMemo(
    () => [
      {
        header: "Feature",
        accessor: ({ name }) => featureFlagPublicData?.definitions?.[name]?.displayName ?? camelCaseToTitleCase(name),
      },
      {
        header: "Enabled",
        accessor: ({ enabled }) => (enabled ? <IconCheck color="emerald" /> : <IconClose color="ruby" />),
        width: 0.2,
      },
      {
        header: "User Setting",
        accessor: ({ reason }) => reason.settings.user ?? "default",
        cell: function UserFeatureFlagSettingCell({ row }) {
          const featureName = row.original.name;
          const reason = row.original.reason;
          const lockedByHigherPrecedenceSetting =
            typeof reason.settings.environment === "boolean" ||
            typeof reason.settings.builder === "boolean" ||
            typeof reason.settings.builderUser === "boolean";
          const perUserEnablementDisallowed =
            featureFlagPublicData?.definitions?.[featureName]?.allowPerUserEnablement === false;
          const locked = lockedByHigherPrecedenceSetting || perUserEnablementDisallowed;
          const disabled = !user?.id || locked || savingFeatureName === featureName;
          let title = "This setting overrides the default for the current user.";
          if (lockedByHigherPrecedenceSetting) {
            title = "This feature is controlled by an environment, builder, or builder-user setting.";
          } else if (perUserEnablementDisallowed) {
            title = "Per-user overrides are disabled for this feature.";
          }

          return (
            <Select
              key={`${featureName}-debug-user-setting`}
              disabled={disabled}
              lockIcon={locked}
              title={title}
              onChange={(selection) => void updateUserFeatureFlag(featureName, selectionToSetting(selection))}
              options={featureFlagSettingOptions as OptionProps<string>[]}
              value={settingToSelection(reason.settings.user, "Default")}
            />
          );
        },
      },
      {
        header: "Reason",
        accessor: ({ reason }) => reason.description,
      },
      {
        header: "Key",
        accessor: ({ name }) => name,
      },
      {
        header: "Debug",
        accessor: ({ reason }) => (
          <span>
            <Popover.Root>
              <Popover.Trigger>
                <Button type="button" btnStyle="tertiary" btnSize="small" className={styles.debugButton}>
                  Debug
                </Button>
              </Popover.Trigger>
              <Popover.Content className={styles.reasonPopover} side="left" align="start">
                <CodeBlock code={reason} className={styles.reasonTooltip} />
              </Popover.Content>
            </Popover.Root>
          </span>
        ),
        width: 0.2,
      },
    ],
    [featureFlagPublicData?.definitions, savingFeatureName, user?.id, updateUserFeatureFlag],
  );

  const featureData = useMemo<FeatureRow[]>(() => {
    const environmentSettingOverrides = featureFlagPublicData?.environmentSettingOverrides;
    const useEnvironmentSettingOverrides = environmentSettingOverrides !== undefined;

    return [...featureNames]
      .sort((a, b) => a.localeCompare(b))
      .map((name) => {
        const environmentValue = environmentSettingOverrides?.[name];
        const [enabled, reason] = featureEnabled(
          userWithFeatureFlagOverrides,
          builder,
          name,
          environmentValue,
          useEnvironmentSettingOverrides,
        );
        return { name, enabled, reason };
      });
  }, [builder, featureFlagPublicData?.environmentSettingOverrides, userWithFeatureFlagOverrides]);

  const filteredFeatureData = useMemo(
    () =>
      (hideDisabled ? featureData.filter(({ enabled }) => enabled) : featureData).filter((feature) => {
        // Hide features that are not set for the environment
        if (!feature.enabled && !!features?.[feature.name]?.hideWhenDisabled) {
          if (feature.reason.settings.environment === false) {
            return false;
          }

          return true;
        }

        return true;
      }),
    [featureData, hideDisabled],
  );

  const productData = useMemo<ProductRow[]>(() => {
    return [...builderProductValues]
      .sort((a, b) => clientBuilderProductDisplayNames[a].localeCompare(clientBuilderProductDisplayNames[b]))
      .map((product) => ({
        name: clientBuilderProductDisplayNames[product],
        product,
        enabled: Boolean(enabledProducts[product]),
      }));
  }, [enabledProducts]);

  const filteredProductData = hideDisabledProducts ? productData.filter(({ enabled }) => enabled) : productData;

  const productColumns: Column<ProductRow>[] = useMemo(
    () => [
      {
        header: "Product",
        accessor: ({ name }) => name,
      },
      {
        header: "Enabled",
        accessor: ({ enabled }) => (enabled ? <IconCheck color="emerald" /> : <IconClose color="ruby" />),
        width: 0.2,
      },
      {
        header: "Key",
        accessor: ({ product }) => product,
      },
    ],
    [],
  );

  const permissionData = useMemo<PermissionRow[]>(() => {
    return getVisiblePermissionGroups(enabledFeatures)
      .flatMap((group) =>
        group.permissions.map((permission) => ({
          group: group.group,
          permission,
          enabled: hasPermission(permission),
        })),
      )
      .sort((a, b) => {
        if (a.group !== b.group) {
          return a.group.localeCompare(b.group);
        }

        return a.permission.localeCompare(b.permission);
      });
  }, [enabledFeatures, hasPermission]);

  const permissionColumns: Column<PermissionRow>[] = useMemo(
    () => [
      {
        header: "Permission",
        accessor: ({ permission }) => formatPermissionName(permission),
      },
      {
        header: "Allowed",
        accessor: ({ enabled }) => (enabled ? <IconCheck color="emerald" /> : <IconClose color="ruby" />),
        width: 0.2,
      },
      {
        header: "Group",
        accessor: ({ group }) => group,
      },
      {
        header: "Key",
        accessor: ({ permission }) => permission,
      },
    ],
    [],
  );

  const visiblePermissionData = hideDeniedPermissions
    ? permissionData.filter(({ enabled }) => enabled)
    : permissionData;

  const assignedRoleNames = useMemo(
    () => [...(builderUser?.roles ?? [])].map((role) => role.name).sort((a, b) => a.localeCompare(b)),
    [builderUser?.roles],
  );

  const impersonatedRoleName = user?.tempUserRole ?? null;

  const availableRoleNames = useMemo(
    () => [...(builderPoliciesAndRoles?.roles ?? [])].map((role) => role.name).sort((a, b) => a.localeCompare(b)),
    [builderPoliciesAndRoles?.roles],
  );

  const enabledFeatureCount = featureData.filter(({ enabled }) => enabled).length;
  const enabledProductCount = productData.filter(({ enabled }) => enabled).length;
  const allowedPermissionCount = permissionData.filter(({ enabled }) => enabled).length;

  const environmentRows = [
    { label: "Environment", value: getEnvironmentDetail(environment.server) },
    { label: "Database", value: getEnvironmentDetail(environment.db) },
    { label: "Branch", value: getEnvironmentDetail(environment.branch) },
    { label: "Release", value: getEnvironmentDetail(environment.tag) },
    {
      label: "Commit",
      value: environment.commit ? getEnvironmentDetail(environment.commit.substring(0, 8)) : FALLBACK_VALUE,
    },
    { label: "Mode", value: environment.developmentMode ? "development" : "production" },
  ];

  const userRows = [
    { label: "Builder ID", value: getEnvironmentDetail(builder?.id) },
    { label: "Current User ID", value: getEnvironmentDetail(user?.id) },
    { label: "Admin Role", value: getEnvironmentDetail(user?.adminRole) },
    { label: "Builder User ID", value: getEnvironmentDetail(builderUser?.id) },
  ];

  const viewButtons: { id: DebugInfoView; label: string }[] = [
    { id: "overview", label: "Overview" },
    { id: "featureFlags", label: "Feature Flags" },
    { id: "products", label: "Products" },
    { id: "access", label: "Permissions & Roles" },
  ];

  const copyFFStateToClipboard = useCallback(async () => {
    const enabledFlags = objectFilter(userWithFeatureFlagOverrides?.featureFlags ?? {}, Boolean);
    setUserNotification(
      await copyTextWithNotification(JSON.stringify(enabledFlags, null, 2), "Feature flag state copied to clipboard"),
    );
  }, [userWithFeatureFlagOverrides]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Debug Console"
      size="large"
      footer={
        <Button type="button" btnStyle="tertiary" btnSize="large" onClick={onClose}>
          Close
        </Button>
      }
      scrollModal
      className={cx(styles.modal, isDarkMode && styles.dark)}
      variants={["bodyPadding", "footerButtons"]}
    >
      <div className={styles.wrapper}>
        <div className={styles.summaryStrip}>
          <div className={styles.summaryCard}>
            <div className={styles.summaryLabel}>Enabled Features</div>
            <div className={styles.summaryValue}>
              {enabledFeatureCount}/{featureData.length}
            </div>
          </div>
          <div className={styles.summaryCard}>
            <div className={styles.summaryLabel}>Enabled Products</div>
            <div className={styles.summaryValue}>
              {enabledProductCount}/{productData.length}
            </div>
          </div>
          <div className={styles.summaryCard}>
            <div className={styles.summaryLabel}>Allowed Permissions</div>
            <div className={styles.summaryValue}>
              {allowedPermissionCount}/{permissionData.length}
            </div>
          </div>
        </div>

        <div className={styles.viewSelector}>
          {viewButtons.map(({ id, label }) => (
            <Button
              key={id}
              type="button"
              btnSize="small"
              btnStyle={view === id ? "secondary" : "tertiary"}
              onClick={() => setView(id)}
            >
              {label}
            </Button>
          ))}
        </div>

        {view === "overview" && (
          <>
            <section className={styles.section}>
              <h3 className={styles.sectionTitle}>Environment</h3>
              <div className={styles.keyValueGrid}>
                {environmentRows.map(({ label, value }) => (
                  <div key={label} className={styles.keyValueRow}>
                    <span className={styles.key}>{label}</span>
                    <span className={styles.value}>{value}</span>
                  </div>
                ))}
              </div>
            </section>

            <section className={styles.section}>
              <h3 className={styles.sectionTitle}>User</h3>
              <div className={styles.keyValueGrid}>
                {userRows.map(({ label, value }) => (
                  <div key={label} className={styles.keyValueRow}>
                    <span className={styles.key}>{label}</span>
                    <span className={styles.value}>{value}</span>
                  </div>
                ))}
              </div>
            </section>
          </>
        )}

        {view === "featureFlags" && (
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Feature Flags</h3>
            <Message messageType="info" variant="banner" className={styles.featureFlagsInfoMessage}>
              Builder-level feature flag settings override builder user and user-level settings when viewing that
              builder.
            </Message>
            <p className={styles.metaText}>
              {enabledFeatureCount} enabled out of {featureData.length} total features.
            </p>
            <div className="table-container">
              <ConfigTable
                controls={{
                  search: true,
                  customControls: (
                    <>
                      <Switch
                        checked={hideDisabled}
                        onChangeBoolean={setHideDisabled}
                        label="Hide disabled"
                        className={styles.inlineSwitch}
                      />
                      <Button type="button" btnStyle="tertiary" btnSize="small" onClick={copyFFStateToClipboard}>
                        <IconCopy size={16} />
                        Copy enabled flags
                      </Button>
                    </>
                  ),
                }}
                columns={featureColumns}
                data={filteredFeatureData}
              />
            </div>
          </section>
        )}

        {view === "products" && (
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Enabled Products</h3>
            <p className={styles.metaText}>
              {enabledProductCount} enabled out of {productData.length} total products.
            </p>
            <div className="table-container">
              <ConfigTable
                controls={{
                  search: true,
                  customControls: (
                    <Switch
                      checked={hideDisabledProducts}
                      onChangeBoolean={setHideDisabledProducts}
                      label="Hide disabled"
                      className={styles.inlineSwitch}
                    />
                  ),
                }}
                columns={productColumns}
                data={filteredProductData}
              />
            </div>
          </section>
        )}

        {view === "access" && (
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Permissions & Roles</h3>

            <div className={styles.roleMetaRow}>
              <div className={styles.roleMetaBlock}>
                <span className={styles.roleMetaLabel}>Assigned Builder Role</span>
                <span className={styles.roleMetaValue}>
                  {assignedRoleNames.length > 0 ? assignedRoleNames.join(", ") : "None"}
                </span>
              </div>
              <div
                className={cx(styles.roleMetaBlock, impersonatedRoleName && styles.roleImpersonationActive)}
                data-active={impersonatedRoleName ? "true" : "false"}
              >
                <span className={styles.roleMetaLabel}>Role Impersonation</span>
                <span className={styles.roleMetaValue}>{impersonatedRoleName ?? "Inactive"}</span>
              </div>
              <div className={styles.roleMetaBlock}>
                <span className={styles.roleMetaLabel}>Builder Role Definitions</span>
                <span className={styles.roleMetaValue}>{availableRoleNames.length}</span>
              </div>
              <div className={styles.roleMetaBlock}>
                <span className={styles.roleMetaLabel}>Builder Policies</span>
                <span className={styles.roleMetaValue}>{builderPoliciesAndRoles?.policies.length ?? 0}</span>
              </div>
            </div>

            <div className="table-container">
              <ConfigTable
                controls={{
                  search: true,
                  customControls: (
                    <Switch
                      checked={hideDeniedPermissions}
                      onChangeBoolean={setHideDeniedPermissions}
                      label="Show only allowed"
                      className={styles.inlineSwitch}
                    />
                  ),
                }}
                columns={permissionColumns}
                data={visiblePermissionData}
              />
            </div>
          </section>
        )}
      </div>
    </Modal>
  );
}
